// SharePoint sources: the libraries and folders on the HR site that HR admins have chosen for
// Ask HR. Nothing outside this list is ever synced. A source covers its folder and everything
// below it.

// Who a source's documents apply to. Every employee falls in exactly one audience
// (audienceOf); "all" reaches every audience, "us" both US ones (not Canada).
export const Applicability = {
  All: "all",
  Us: "us",
  California: "us-ca",
  UsOther: "us-other",
  Canada: "canada",
} as const;

export type ApplicabilityValue = (typeof Applicability)[keyof typeof Applicability];
export type Audience = "us-ca" | "us-other" | "canada";
export const AUDIENCES: readonly Audience[] = ["us-ca", "us-other", "canada"];

export const APPLICABILITY_LABELS: Record<ApplicabilityValue, string> = {
  all: "All employees",
  us: "US only",
  "us-ca": "California only",
  "us-other": "US outside California",
  canada: "Canada only",
};

// An employee's audience from their Entra attributes. Careful: "CA" means Canada in
// usageLocation (an ISO country code) and California in state. Novara has country empty for
// everyone; usageLocation is the reliable country field (5 Canada, 199 US on 2026-10-08).
export function audienceOf(user: { usageLocation?: string | null; country?: string | null; state?: string | null }): Audience {
  const country = (user.usageLocation ?? "").trim().toUpperCase();
  const countryName = (user.country ?? "").trim().toLowerCase();
  if (country === "CA" || countryName === "canada") return "canada";
  const state = (user.state ?? "").trim().toLowerCase();
  if (state === "ca" || state === "california") return "us-ca";
  return "us-other";
}

// Where someone works, as they told Ask HR: the country, and for the US the state (USPS code).
// The state decides the audience today (California or not) and is kept so state-specific
// documents can be added without asking everyone again.
export type Country = "US" | "CA";
export interface WorkLocation {
  country: Country;
  state?: string;
}

export const US_STATES: Readonly<Record<string, string>> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado", CT: "Connecticut",
  DE: "Delaware", DC: "District of Columbia", FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana",
  NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah",
  VT: "Vermont", VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

const isState = (v: unknown): v is string => typeof v === "string" && Object.hasOwn(US_STATES, v);

export function isWorkLocation(v: unknown): v is WorkLocation {
  if (typeof v !== "object" || v === null) return false;
  const l = v as Record<string, unknown>;
  if (l.country === "CA") return l.state === undefined;
  return l.country === "US" && isState(l.state);
}

// Only the fields a WorkLocation has, so a stored or posted object carries nothing else.
export function workLocation(v: WorkLocation): WorkLocation {
  return v.country === "CA" ? { country: "CA" } : { country: "US", state: v.state! };
}

export function audienceForLocation(l: WorkLocation): Audience {
  if (l.country === "CA") return "canada";
  return l.state === "CA" ? "us-ca" : "us-other";
}

export function placeName(l: WorkLocation): string {
  return l.country === "CA" ? "Canada" : (US_STATES[l.state ?? ""] ?? "the US");
}

// A place as one string, for button and menu values: "US-TX", or "CANADA" (not "CA", which is
// California). placeFromValue also takes a bare state code, as older Slack prompts sent.
export function placeValue(l: WorkLocation): string {
  return l.country === "CA" ? "CANADA" : `US-${l.state}`;
}

export function placeFromValue(v: unknown): WorkLocation | null {
  if (typeof v !== "string") return null;
  if (v === "CANADA") return { country: "CA" };
  const code = v.startsWith("US-") ? v.slice(3) : v;
  return isState(code) ? { country: "US", state: code } : null;
}

// Two-letter codes that are also everyday words; typed alone they aren't taken as a state.
const WORD_CODES = new Set(["OK", "HI", "ME", "IN", "OR", "OH"]);

// What someone typed when asked where they live, if it names one place: "Texas", "new york.",
// "TX", "I live in Ohio", "Canada". Questions, long text, or text naming more than one place
// give null, so a question typed instead of an answer is kept as a question.
export function parsePlace(text: string): WorkLocation | null {
  // Length first, and no regex for the trailing punctuation: this runs on whatever is typed.
  let t = text.trim();
  if (t.length > 60) return null;
  while (t.endsWith(".") || t.endsWith("!")) t = t.slice(0, -1);
  if (!t || t.length > 40 || t.includes("?")) return null;
  if (/^[A-Z]{2}$/.test(t)) return isState(t) && !WORD_CODES.has(t) ? { country: "US", state: t } : null;
  const words = ` ${t.toLowerCase().replace(/[^a-z ]+/g, " ").replace(/\s+/g, " ")} `;
  const found: WorkLocation[] = Object.entries(US_STATES)
    .filter(([, name]) => words.includes(` ${name.toLowerCase()} `))
    .map(([code]) => ({ country: "US", state: code }));
  // "West Virginia" also contains "Virginia": keep the longer name.
  const named = found.filter((a) => !found.some((b) => b !== a && US_STATES[b.state!]!.includes(US_STATES[a.state!]!)));
  if (words.includes(" canada ")) named.push({ country: "CA" });
  return named.length === 1 ? named[0]! : null;
}

// The HR record's location, when it says enough to pick one: Canada from usageLocation, or a US
// state (Entra holds a code or a full name). Nothing for a US employee with no state.
export function hrLocation(user: { usageLocation?: string | null; country?: string | null; state?: string | null }): WorkLocation | null {
  if (audienceOf(user) === "canada") return { country: "CA" };
  const raw = (user.state ?? "").trim();
  const code = raw.toUpperCase();
  if (isState(code)) return { country: "US", state: code };
  const byName = Object.entries(US_STATES).find(([, name]) => name.toLowerCase() === raw.toLowerCase());
  return byName ? { country: "US", state: byName[0] } : null;
}

// What an answer knows about where the asker works, and how it knows:
//   chosen   they told Ask HR (asked once, on their first question; changeable on the web)
//   hr       from their Entra record (state, usageLocation); only until they choose
//   unknown  neither
export type LocationSource = "hr" | "chosen" | "unknown";

export interface AudienceInfo {
  audience: Audience;
  known: boolean;
  source: LocationSource;
  hr: Audience | null; // what the HR record says, when it says anything
  place: WorkLocation | null; // chosen, else from the HR record
  hrPlace: WorkLocation | null;
}

export const AUDIENCE_LABELS: Record<Audience, string> = {
  "us-ca": "California",
  "us-other": "Another US state",
  canada: "Canada",
};

export function isAudience(v: unknown): v is Audience {
  return v === "us-ca" || v === "us-other" || v === "canada";
}

export function audienceInfo(
  user: { usageLocation?: string | null; country?: string | null; state?: string | null },
  chosen?: WorkLocation | null,
): AudienceInfo {
  const fromHr = audienceOf(user);
  const hrKnown = fromHr !== "us-other" || Boolean((user.state ?? "").trim());
  const hr = hrKnown ? fromHr : null;
  const hrPlace = hrLocation(user);
  if (chosen) return { audience: audienceForLocation(chosen), known: true, source: "chosen", hr, place: chosen, hrPlace };
  if (hr) return { audience: hr, known: true, source: "hr", hr, place: hrPlace, hrPlace };
  return { audience: "us-other", known: false, source: "unknown", hr: null, place: null, hrPlace: null };
}

// What the web page and Slack show about it.
export interface LocationSummary {
  audience: Audience | null; // null: not known
  source: LocationSource;
  hr: Audience | null;
  place: WorkLocation | null;
  hrPlace: WorkLocation | null; // offered first when they're asked
}

export function locationSummary(info: AudienceInfo): LocationSummary {
  return { audience: info.known ? info.audience : null, source: info.source, hr: info.hr, place: info.place, hrPlace: info.hrPlace };
}

// The audiences whose documents an answer may draw on.
export function readableAudiences(info: AudienceInfo): Audience[] {
  return info.known ? [info.audience] : [...AUDIENCES];
}

export function appliesTo(applicability: ApplicabilityValue, audience: Audience): boolean {
  return applicability === "all" || applicability === audience || (applicability === "us" && audience !== "canada");
}

// itemId is "root" when the source is a whole library.
export const ROOT = "root";

// A web source (web.ts) uses the same record: driveId "web", itemId a hash of the URL, driveName
// its display name, path "/", webUrl the address people open, url what sync fetches.
export interface SharePointSource {
  kind?: "sharepoint" | "web"; // absent on sources added before websites: SharePoint
  feed?: "lever" | "page"; // web only
  url?: string; // web only
  id: string;
  siteId?: string; // SharePoint only; absent on sources added before there was more than one site: the HR site
  driveId: string;
  driveName: string;
  itemId: string;
  path: string; // "/" for a whole library, else "/Policies/2026"
  webUrl: string;
  applicability: ApplicabilityValue;
  addedBy: string;
  addedAt: string;
}

export interface SourceInput {
  siteId?: string;
  driveId: string;
  itemId: string;
  applicability: ApplicabilityValue;
}

export class BadRequestError extends Error {
  readonly status = 400;
  constructor(message: string) {
    super(message);
    this.name = "BadRequestError";
  }
}

// Graph drive and item IDs: letters, digits and a few separators ("b!Xy_3-..."). Anything else
// is refused before it reaches a Graph URL.
const GRAPH_ID = /^[A-Za-z0-9!_-]{1,200}$/;

export function isGraphId(v: unknown): v is string {
  return typeof v === "string" && GRAPH_ID.test(v);
}

export function isApplicability(v: unknown): v is ApplicabilityValue {
  return typeof v === "string" && Object.hasOwn(APPLICABILITY_LABELS, v);
}

export function parseSourceInput(body: unknown): SourceInput {
  const b = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;
  if (!isGraphId(b.driveId)) throw new BadRequestError("driveId is missing or invalid");
  const itemId = b.itemId ?? ROOT;
  if (!isGraphId(itemId)) throw new BadRequestError("itemId is invalid");
  const applicability = b.applicability ?? Applicability.All;
  if (!isApplicability(applicability)) throw new BadRequestError("applicability must be all, us, us-ca, us-other or canada");
  if (b.siteId !== undefined && typeof b.siteId !== "string") throw new BadRequestError("siteId is invalid");
  return { ...(b.siteId === undefined ? {} : { siteId: b.siteId }), driveId: b.driveId, itemId, applicability };
}

export function sourceId(driveId: string, itemId: string): string {
  return `${driveId}~${itemId}`;
}

export function parseSourceId(id: string): { driveId: string; itemId: string } {
  const [driveId, itemId, extra] = id.split("~");
  if (!isGraphId(driveId) || !isGraphId(itemId) || extra !== undefined) throw new BadRequestError("invalid source id");
  return { driveId, itemId };
}

// The source that already includes this folder, if any: the same folder, or one above it in the
// same library. Used to show "Included" in the picker and to refuse duplicates.
export function coveringSource<T extends Pick<SharePointSource, "driveId" | "path">>(
  sources: readonly T[],
  driveId: string,
  path: string,
): T | undefined {
  return sources
    .filter((s) => s.driveId === driveId && (s.path === "/" || s.path === path || path.startsWith(`${s.path}/`)))
    .sort((a, b) => a.path.length - b.path.length)[0];
}

// Sources below this one in the same library, which a new source at this path would make
// redundant. The UI offers to replace them.
export function coveredSources<T extends Pick<SharePointSource, "driveId" | "path">>(
  sources: readonly T[],
  driveId: string,
  path: string,
): T[] {
  return sources.filter(
    (s) => s.driveId === driveId && s.path !== path && (path === "/" || s.path.startsWith(`${path}/`)),
  );
}
